import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { ArrowRight, Clock, CreditCard, Eye, Lightbulb, Lock, Phone, ShieldCheck, Wrench, Zap } from 'lucide-react';

const valeurs = [
  {
    icon: Zap,
    title: 'Simplicité',
    desc: "Une plateforme intuitive qui simplifie l'organisation de concours. Pas besoin d'être expert en informatique pour digitaliser vos compétitions.",
    color: 'bg-amber-50 text-amber-600',
    border: 'border-amber-100',
  },
  {
    icon: Eye,
    title: 'Transparence',
    desc: 'Des classements publics, un historique complet et une communication claire. Chaque joueur et organisateur a accès à toutes les informations.',
    color: 'bg-blue-50 text-blue-600',
    border: 'border-blue-100',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    desc: 'Nous repoussons les limites du possible pour les sports de boules. Export compatible FFPJP, paiements en ligne, rappels automatiques J-7/J-1 : le futur est là.',
    color: 'bg-emerald-50 text-emerald-600',
    border: 'border-emerald-100',
  },
];

const equipe = [
  {
    initials: 'MAD',
    name: 'Marc-Antoine DUTOIT',
    role: 'CEO/CTO & Co-fondateur',
    desc: "Fondateur de MADSAAS, Marc-Antoine conçoit et développe BoulesConnect pour moderniser l'organisation des concours de pétanque, de jeu provençal et de boule lyonnaise.",
    gradient: 'from-blue-500 to-indigo-600',
  },
];

const engagements = [
  {
    icon: ShieldCheck,
    value: 'FFPJP',
    label: 'Licences supportées',
    desc: 'Profils compatibles FFPJP avec numéro de licence, catégorie et classification.',
  },
  {
    icon: CreditCard,
    value: 'Stripe',
    label: 'Paiements sécurisés',
    desc: "Encaissements et reversements via Stripe Connect Standard (Stripe Payments Europe Limited, agréé par la Banque Centrale d'Irlande).",
  },
  {
    icon: Lock,
    value: 'RGPD',
    label: 'Données protégées',
    desc: 'Hébergement en France, chiffrement des données, respect total du RGPD.',
  },
  {
    icon: Clock,
    value: '24/7',
    label: 'Toujours ouvert',
    desc: 'Inscriptions, paiements et consultation des concours accessibles à tout moment.',
  },
];

export default function APropos() {
  return (
    <>
      <PageMeta
        title='À propos'
        description='Découvrez BoulesConnect, la plateforme française qui digitalise les concours de pétanque, de jeu provençal et de boule lyonnaise. Notre mission, nos valeurs et notre équipe.'
      />

      {/* Hero */}
      <section className='pt-40 pb-0 px-6 bg-linear-to-b from-blue-50/50 to-white'>
        <div className='max-w-4xl mx-auto text-center'>
          <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>À propos</span>
          <h1 className='text-4xl md:text-6xl font-extrabold text-slate-900 mt-4 mb-6'>
            À propos de <span className='gradient-text'>BoulesConnect</span>
          </h1>
          <p className='text-xl text-slate-500 max-w-2xl mx-auto'>La plateforme qui digitalise les sports de boules</p>
        </div>
      </section>

      {/* Notre histoire */}
      <section className='pt-20 pb-20 px-6'>
        <div className='max-w-4xl mx-auto'>
          <div className='space-y-12'>
            {/* Bloc 1 — L'origine */}
            <div className='bg-white rounded-3xl border border-slate-200 p-8 md:p-12 shadow-sm'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-6 flex items-center gap-3'>
                <div className='w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center'>
                  <Phone className='w-5 h-5 text-blue-600' />
                </div>
                Tout a commencé par un coup de fil
              </h2>
              <div className='space-y-4 text-slate-600 leading-relaxed text-lg'>
                <p>
                  En <strong className='text-slate-900'>2021</strong>,{' '}
                  <strong className='text-slate-900'>Philippe Cochet</strong> et{' '}
                  <strong className='text-slate-900'>Georges Ventre</strong>, organisateurs du{' '}
                  <strong className='text-slate-900'>SuperChallenge</strong> — le plus grand circuit de{' '}
                  <strong className='text-slate-900'>jeu provençal</strong> (la « longue ») de la région PACA, fédérant
                  les grands prix des clubs du Sud — cherchaient une solution pour gérer leurs concours : inscriptions,
                  paiements, listes de joueurs, gestion des equipes.
                </p>
                <p>
                  Le constat était simple :{' '}
                  <strong className='text-slate-900'>
                    aucun logiciel sur le marché ne répondait aux besoins spécifiques des organisateurs de concours de
                    boules.
                  </strong>
                </p>
              </div>
            </div>

            {/* Bloc 2 — La première solution */}
            <div className='bg-white rounded-3xl border border-slate-200 p-8 md:p-12 shadow-sm'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-6 flex items-center gap-3'>
                <div className='w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center'>
                  <Wrench className='w-5 h-5 text-emerald-600' />
                </div>
                Une réponse pragmatique
              </h2>
              <div className='space-y-4 text-slate-600 leading-relaxed text-lg'>
                <p>
                  Après plusieurs recherches et échanges, ils se tournent vers{' '}
                  <strong className='text-slate-900'>
                    <a
                      href='https://madsaas.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='hover:text-blue-600'
                    >
                      MADSAAS
                    </a>
                  </strong>
                  , studio de développement web et SaaS fondé par{' '}
                  <strong className='text-slate-900'>
                    <a
                      href='https://marcantoinedutoit.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='hover:text-blue-600'
                    >
                      Marc-Antoine Dutoit
                    </a>
                  </strong>
                  .
                </p>
                <p>
                  La première réponse a été pragmatique : intégrer la gestion des concours et des paiements directement
                  dans la plateforme SaaS de gestion d'entreprise existante. Le SuperChallenge a pu s'organiser plus
                  sereinement, avec des inscriptions en ligne, un suivi des paiements en temps réel et une vision claire
                  des équipes inscrites.
                </p>
              </div>
            </div>

            {/* Bloc 3 — Le déclic */}
            <div className='bg-linear-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white shadow-xl'>
              <h2 className='text-2xl font-extrabold mb-6 flex items-center gap-3'>
                <div className='w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center'>
                  <Lightbulb className='w-5 h-5' />
                </div>
                Le déclic
              </h2>
              <div className='space-y-4 text-blue-100 leading-relaxed text-lg'>
                <p>
                  Très vite, une évidence s'est imposée :{' '}
                  <strong className='text-white'>
                    si le SuperChallenge avait ce problème, des centaines d'organisateurs en France vivaient la même
                    galère
                  </strong>{' '}
                  — des feuilles volantes, des tableaux Excel bricolés, des paiements en espèces impossibles à tracer,
                  des joueurs qui appellent le samedi soir pour savoir s'il reste de la place.
                </p>
                <p className='text-white font-bold text-xl'>BoulesConnect est née de ce constat terrain.</p>
                <p>
                  Pas d'un PowerPoint, pas d'une étude de marché théorique — mais d'un{' '}
                  <strong className='text-white'>vrai besoin</strong>, exprimé par de vrais organisateurs, résolu une
                  première fois de manière artisanale, puis transformé en une plateforme dédiée, pensée pour le monde de
                  la <strong className='text-white'>pétanque, du jeu provençal et de la boule lyonnaise</strong>.
                </p>
              </div>
            </div>

            {/* Bloc 4 — Mission */}
            <div className='bg-white rounded-3xl border border-slate-200 p-8 md:p-12 shadow-sm text-center'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-6'>Notre mission</h2>
              <p className='text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-6'>
                Permettre à chaque club, chaque organisateur et chaque joueur de vivre sa passion des sports de boules{' '}
                <strong className='text-slate-900'>sans les contraintes administratives</strong>.
              </p>
              <p className='text-2xl font-bold gradient-text'>
                Les sports de boules, c'est le plaisir du jeu, la convivialité, le partage.
                <br />
                Le reste, BoulesConnect s'en occupe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nos valeurs */}
      <section className='px-6 mb-20'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-12'>
            <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>Nos valeurs</span>
            <h2 className='text-3xl md:text-4xl font-extrabold text-slate-900 mt-3'>Ce qui nous guide au quotidien</h2>
          </div>
          <div className='grid md:grid-cols-3 gap-8'>
            {valeurs.map(valeur => (
              <div
                key={valeur.title}
                className={`bg-white rounded-3xl shadow-sm border ${valeur.border} p-8 text-center hover:shadow-lg hover:-translate-y-1 transition-all`}
              >
                <div className={`w-16 h-16 rounded-2xl ${valeur.color} flex items-center justify-center mx-auto mb-6`}>
                  <valeur.icon className='w-8 h-8' />
                </div>
                <h3 className='text-xl font-extrabold text-slate-900 mb-3'>{valeur.title}</h3>
                <p className='text-sm text-slate-500 leading-relaxed'>{valeur.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* L'équipe */}
      <section className='px-6 mb-20'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-12'>
            {/*<span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>L'équipe</span>*/}
            <h2 className='text-3xl md:text-4xl font-extrabold text-slate-900 mt-3'>
              La personne derrière BoulesConnect
            </h2>
          </div>
          <div className='grid md:grid-cols-1 lg:grid-cols-3 gap-8'>
            {equipe.map(member => (
              <div
                key={member.name}
                className='bg-white rounded-3xl shadow-sm border border-slate-100 p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all'
              >
                <div
                  className={`w-20 h-20 rounded-2xl bg-linear-to-br ${member.gradient} flex items-center justify-center mx-auto mb-4`}
                >
                  <span className='text-2xl font-extrabold text-white'>{member.initials}</span>
                </div>
                <h3 className='text-lg font-bold text-slate-900 mb-1'>{member.name}</h3>
                <p className='text-sm font-medium text-blue-600 mb-3'>{member.role}</p>
                <p className='text-xs text-slate-500 leading-relaxed'>{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos engagements */}
      <section className='px-6 mb-20'>
        <div className='max-w-5xl mx-auto'>
          <div className='bg-linear-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12'>
            <div className='text-center mb-10'>
              <span className='text-blue-400 font-bold text-sm uppercase tracking-wider'>Nos engagements</span>
              <h2 className='text-2xl md:text-3xl font-extrabold text-white mt-3'>
                Une plateforme conçue pour la confiance
              </h2>
            </div>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-8'>
              {engagements.map(item => (
                <div key={item.label} className='text-center'>
                  <div className='w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center mx-auto mb-4'>
                    <item.icon className='w-6 h-6 text-blue-400' />
                  </div>
                  <div className='text-2xl md:text-3xl font-extrabold text-white mb-1'>{item.value}</div>
                  <div className='text-sm font-semibold text-blue-300 mb-2'>{item.label}</div>
                  <p className='text-xs text-slate-400 leading-relaxed'>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-linear-to-r from-blue-600 via-indigo-600 to-purple-700 rounded-3xl p-8 md:p-12 text-center'>
            <h2 className='text-2xl md:text-3xl font-extrabold text-white mb-3'>Rejoignez l'aventure</h2>
            <p className='text-blue-100 mb-8 max-w-lg mx-auto'>
              Que vous soyez joueur, président de club ou organisateur de concours, BoulesConnect est fait pour vous.
            </p>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to={ROUTES.REGISTER}
                className='inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-700 rounded-2xl font-bold text-sm hover:bg-blue-50 transition-colors'
              >
                Créer mon compte gratuit <ArrowRight className='w-4 h-4' />
              </Link>
              <Link
                to={ROUTES.CONTACT}
                className='inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 backdrop-blur border border-white/20 text-white rounded-2xl font-bold text-sm hover:bg-white/20 transition-colors'
              >
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
